<!-- eslint-disable vue/valid-v-for -->
<template>
  <v-sheet flat border class="pa-4 col-sm-12">
    <p class="text-subtitle-1 pt-3 text-primary mb-3">
      <span class="font-weight-bold">Pricing Demand Management</span>
      - Input values at execution time
    </p>
    <v-row>
      <v-col cols="12">
        <v-select
          v-model="localInputData.ControlId"
          :items="inputList"
          :item-title="(item) => item.Name || ''"
          :item-value="(item) => item.Id"
          label="Control Name"
          variant="outlined"
          density="compact"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.CurrentDateTime"
          :rules="[isDate]"
          variant="outlined"
          density="compact"
          label="Current Date/Time"
          type="datetime-local"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.ReadingDateTime"
          :rules="[isDate]"
          variant="outlined"
          density="compact"
          label="Reading Date/Time"
          type="datetime-local"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.DelayInMinutes"
          :rules="[isNumberGreaterThanZero]"
          variant="outlined"
          density="compact"
          label="Delay in Minutes"
        />
      </v-col>
    </v-row>
    <BusinessHoursInput
      v-model:start-time="localInputData.SiteStartTime"
      v-model:end-time="localInputData.SiteEndTime"
    />
    <v-row>
      <v-col sm="4">
        <v-text-field
          v-model="localInputData.HeatSetting"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Heat Setting"
        />
      </v-col>
      <v-col sm="4">
        <v-text-field
          v-model="localInputData.MinHeatSetting"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Min Heat Setting"
        />
      </v-col>
      <v-col sm="4">
        <v-text-field
          v-model="localInputData.MaxHeatSetting"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Max Heat Setting"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <p class="text-subtitle-2 pt-3 text-primary">
          Note: the following may not directly represent variables
          <em>available</em> in the rule, but power other variables used within
          the rule.
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col sm="3">
        <v-text-field
          v-model="localInputData.CoolSetting"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Cool Setting"
        />
      </v-col>
      <v-col sm="3">
        <v-text-field
          v-model="localInputData.MinCoolSetting"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Min Cool Setting"
        />
      </v-col>
      <v-col sm="3">
        <v-text-field
          v-model="localInputData.MaxCoolSetting"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Max Cool Setting"
        />
      </v-col>
      <v-col sm="3">
        <v-text-field
          v-model="localInputData.FanStatus"
          :rules="[(v) => /^(on|off)$/i.test(v)]"
          variant="outlined"
          density="compact"
          label="Fan On/Auto Status"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col sm="3">
        <v-text-field
          v-model="localInputData.System"
          :rules="[(v) => /^(auto|off|cool|heat)$/i.test(v)]"
          variant="outlined"
          density="compact"
          label="The Current System Value of the A/C Unit - auto/off/cool/heat"
        />
      </v-col>
      <v-col sm="3">
        <v-text-field
          v-model="localInputData.FrontKeyPad"
          :rules="[(v) => /^(on|off)$/i.test(v)]"
          variant="outlined"
          density="compact"
          label="Font Key Pad On/Off Value"
        />
      </v-col>
      <v-col sm="3">
        <v-text-field
          v-model="localInputData.CurrentHour"
          :rules="[isValid24HourFormat]"
          variant="outlined"
          density="compact"
          label="Current Hour of the Site (at run time of strategy) -- in 24hr"
        />
      </v-col>
    </v-row>
  </v-sheet>
</template>
<script>
import {
  isNumber,
  isNumberGreaterThanZero,
  hasValue,
  isDate,
  isValid24HourFormat,
} from '@/Core.Patterns/Factory/validationFactory.ts';
import BusinessHoursInput from '@/Core.Service.Domain/Strategies/Strategies/Components/BusinessHoursInput.vue';
import { withBaseInputDefaults } from '@/Core.Service.Domain/Strategies/Strategies/Utils/baseInputDefaults.ts';

export default {
  name: 'PricingDemandManagementInput',

  components: { BusinessHoursInput },

  data() {
    return {
      thermostats: [],
      localInputData: withBaseInputDefaults(this.InputData),
    };
  },

  computed: {
    inputList() {
      // see if we have a list of datasources of this very object in datasources
      const inputs = this.DataSources.find(
        (ds) => ds.Key === 'Thermostats'
      )?.Value;
      if (inputs) return inputs;
      // if not, we'll create one based on InputData.ControlId/Name/FriendlyName values
      return [
        {
          Id: this.InputData.ControlId,
          DeviceName: this.InputData.ControlName,
          FriendlyName: this.InputData.ControlFriendlyName,
          Name: `${this.InputData.ControlFriendlyName} ( ${this.InputData.ControlName} )`,
        },
      ];
    },
  },

  props: {
    InputData: {
      type: Object,
      required: true,
    },
    DataSources: {
      type: Array,
      required: true,
    },
  },

  watch: {
    localInputData: {
      handler(newVal) {
        // this handles the case where a user changes an entry in the Controls v-select
        // since we only get the ControlId (v-model), we need to set the rest
        const selected = this.inputList.find((i) => i.Id == newVal.ControlId);
        if (selected) {
          newVal.ControlName = selected.Name;
          newVal.ControlFriendlyName = selected.FriendlyName;
        } else {
          newVal.ControlName = null;
          newVal.ControlFriendlyName = null;
        }
        this.$emit('update:model', newVal);
      },
      deep: true,
    },
  },

  methods: {
    isNumber,
    isNumberGreaterThanZero,
    hasValue,
    isDate,
    isValid24HourFormat,
  },
};
</script>
